import { useState, useEffect, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { format } from 'date-fns'
import { 
  HiChevronUp, 
  HiChevronDown, 
  HiPencil,
  HiCheck,
  HiX,
  HiPlus,
  HiFilter,
  HiSearch
} from 'react-icons/hi'
import { tasksApi, authApi } from '../../api/client'
import CreateTaskModal from './CreateTaskModal'

const PRIORITY_CONFIG = {
  critical: { color: 'text-red-600 bg-red-50 border-red-200', label: 'Critical', order: 4 },
  high: { color: 'text-orange-600 bg-orange-50 border-orange-200', label: 'High', order: 3 },
  medium: { color: 'text-yellow-600 bg-yellow-50 border-yellow-200', label: 'Medium', order: 2 },
  low: { color: 'text-green-600 bg-green-50 border-green-200', label: 'Low', order: 1 },
}

export default function TableView({ board, onUpdate }) {
  const [columns, setColumns] = useState([])
  const [loading, setLoading] = useState(true)
  const [sortConfig, setSortConfig] = useState({ key: 'title', direction: 'asc' })
  const [searchQuery, setSearchQuery] = useState('')
  const [filterPriority, setFilterPriority] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [editingCell, setEditingCell] = useState(null)
  const [editValue, setEditValue] = useState('')
  const [showCreateTask, setShowCreateTask] = useState(false)
  const [orgUsers, setOrgUsers] = useState([])

  const fetchBoardData = useCallback(async () => {
    if (!board?.id) return
    try {
      setLoading(true)
      const response = await tasksApi.getBoardTasks(board.id)
      // API returns { board, columns } - extract columns array
      const data = Array.isArray(response.data?.columns) ? response.data.columns : []
      setColumns(data.map(col => ({ ...col, tasks: Array.isArray(col.tasks) ? col.tasks : [] })))
    } catch (error) {
      console.error('Failed to fetch board data:', error)
    } finally {
      setLoading(false)
    }
  }, [board?.id])

  useEffect(() => {
    fetchBoardData()
  }, [fetchBoardData])

  useEffect(() => {
    async function fetchUsers() {
      try {
        const res = await authApi.listUsers()
        setOrgUsers(Array.isArray(res.data) ? res.data : [])
      } catch (err) {
        console.error('Failed to fetch users:', err)
      }
    }
    fetchUsers()
  }, [])

  // Flatten tasks with column info
  const allTasks = useMemo(() => {
    return columns.flatMap(col => 
      (col.tasks || []).map(task => ({ ...task, columnName: col.name, columnId: col.id }))
    )
  }, [columns])

  // Filter tasks
  const filteredTasks = useMemo(() => {
    return allTasks.filter(task => {
      const matchesSearch = !searchQuery || 
        task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        task.description?.toLowerCase().includes(searchQuery.toLowerCase())
      
      const matchesPriority = !filterPriority || task.priority === filterPriority
      const matchesStatus = !filterStatus || task.columnName === filterStatus

      return matchesSearch && matchesPriority && matchesStatus
    })
  }, [allTasks, searchQuery, filterPriority, filterStatus])

  // Sort tasks
  const sortedTasks = useMemo(() => {
    const sorted = [...filteredTasks].sort((a, b) => {
      let aVal, bVal

      switch (sortConfig.key) {
        case 'title':
          aVal = a.title.toLowerCase()
          bVal = b.title.toLowerCase()
          break
        case 'priority':
          aVal = PRIORITY_CONFIG[a.priority]?.order || 0
          bVal = PRIORITY_CONFIG[b.priority]?.order || 0
          break
        case 'due_date':
          aVal = a.due_date ? new Date(a.due_date).getTime() : Infinity
          bVal = b.due_date ? new Date(b.due_date).getTime() : Infinity
          break
        case 'status':
          aVal = a.columnName.toLowerCase()
          bVal = b.columnName.toLowerCase()
          break
        case 'assignee':
          aVal = a.assignee?.display_name?.toLowerCase() || 'zzz'
          bVal = b.assignee?.display_name?.toLowerCase() || 'zzz'
          break
        default:
          return 0
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })
    return sorted
  }, [filteredTasks, sortConfig])

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }))
  }

  // Optimistically update a task in local columns state
  const updateTaskLocally = useCallback((taskId, updates) => {
    setColumns(prev => prev.map(col => ({
      ...col,
      tasks: col.tasks.map(t => t.id === taskId ? { ...t, ...updates } : t)
    })))
  }, [])

  // Move a task between columns optimistically
  const moveTaskLocally = useCallback((taskId, fromColId, toColId) => {
    setColumns(prev => {
      let movedTask = null
      const updated = prev.map(col => {
        if (col.id === fromColId) {
          const task = col.tasks.find(t => t.id === taskId)
          if (task) movedTask = { ...task }
          return { ...col, tasks: col.tasks.filter(t => t.id !== taskId) }
        }
        return col
      })
      if (movedTask) {
        return updated.map(col => {
          if (col.id === toColId) {
            return { ...col, tasks: [...col.tasks, movedTask] }
          }
          return col
        })
      }
      return updated
    })
  }, [])

  const handleStartEdit = (taskId, field, currentValue) => {
    setEditingCell({ taskId, field })
    setEditValue(currentValue || '')
  }

  const handleSaveEdit = async (taskId) => {
    if (!editingCell) return
    const field = editingCell.field
    const value = editValue
    setEditingCell(null)
    setEditValue('')
    // Optimistic update
    updateTaskLocally(taskId, { [field]: value })
    try {
      await tasksApi.update(taskId, { [field]: value })
    } catch (error) {
      console.error('Failed to update task:', error)
      fetchBoardData() // Revert on failure
    }
  }

  const handleCancelEdit = () => {
    setEditingCell(null)
    setEditValue('')
  }

  const handleStatusChange = async (taskId, newColumnId) => {
    // Find current column
    const currentCol = columns.find(col => col.tasks.some(t => t.id === taskId))
    if (currentCol?.id === newColumnId) return
    // Optimistic move
    if (currentCol) moveTaskLocally(taskId, currentCol.id, newColumnId)
    try {
      await tasksApi.update(taskId, { column_id: newColumnId })
    } catch (error) {
      console.error('Failed to update task:', error)
      fetchBoardData()
    }
  }

  const handlePriorityChange = async (taskId, newPriority) => {
    updateTaskLocally(taskId, { priority: newPriority })
    try {
      await tasksApi.update(taskId, { priority: newPriority })
    } catch (error) {
      console.error('Failed to update task:', error)
      fetchBoardData()
    }
  }

  const handleAssigneeChange = async (taskId, userId) => {
    // Optimistic update with user info
    const assignedUser = userId ? orgUsers.find(u => u.id === userId) : null
    updateTaskLocally(taskId, {
      assignee_id: userId || null,
      assignee: assignedUser ? { id: assignedUser.id, display_name: assignedUser.display_name, email: assignedUser.email } : null,
    })
    try {
      await tasksApi.update(taskId, { assignee_id: userId || null })
    } catch (error) {
      console.error('Failed to update assignee:', error)
      fetchBoardData()
    }
  }

  const handleCreateTask = async (taskData) => {
    try {
      const firstColumn = columns[0]
      await tasksApi.create({
        ...taskData,
        project_id: board.project_id,
        board_id: board.id,
        column_id: firstColumn?.id,
      })
      setShowCreateTask(false)
      fetchBoardData()
      onUpdate?.()
    } catch (error) {
      console.error('Failed to create task:', error)
    }
  }

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <HiChevronUp className="w-4 h-4 text-surface-300" />
    }
    return sortConfig.direction === 'asc' 
      ? <HiChevronUp className="w-4 h-4 text-brand-600" />
      : <HiChevronDown className="w-4 h-4 text-brand-600" />
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full"></div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-4 mb-4 px-2">
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <HiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 text-sm border border-surface-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 w-64"
            />
          </div>

          {/* Priority Filter */}
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="px-3 py-2 text-sm border border-surface-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value="">All Priorities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          {/* Status Filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 text-sm border border-surface-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
          >
            <option value="">All Statuses</option>
            {columns.map(col => (
              <option key={col.id} value={col.name}>{col.name}</option>
            ))}
          </select>
        </div>

        <button
          onClick={() => setShowCreateTask(true)}
          className="btn btn-primary"
        >
          <HiPlus className="w-4 h-4 mr-2" />
          Add Task
        </button>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto border border-surface-200 dark:border-surface-700 rounded-xl bg-white dark:bg-surface-800">
        <table className="w-full">
          <thead className="bg-surface-50 dark:bg-surface-800/80 sticky top-0 z-10">
            <tr>
              <th className="text-left px-4 py-3 text-sm font-semibold text-surface-700 border-b border-surface-200">
                <button
                  onClick={() => handleSort('title')}
                  className="flex items-center gap-1 hover:text-brand-600 transition-colors"
                >
                  Title
                  <SortIcon columnKey="title" />
                </button>
              </th>
              <th className="text-left px-4 py-3 text-sm font-semibold text-surface-700 border-b border-surface-200 w-32">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center gap-1 hover:text-brand-600 transition-colors"
                >
                  Status
                  <SortIcon columnKey="status" />
                </button>
              </th>
              <th className="text-left px-4 py-3 text-sm font-semibold text-surface-700 border-b border-surface-200 w-28">
                <button
                  onClick={() => handleSort('priority')}
                  className="flex items-center gap-1 hover:text-brand-600 transition-colors"
                >
                  Priority
                  <SortIcon columnKey="priority" />
                </button>
              </th>
              <th className="text-left px-4 py-3 text-sm font-semibold text-surface-700 border-b border-surface-200 w-32">
                <button
                  onClick={() => handleSort('due_date')}
                  className="flex items-center gap-1 hover:text-brand-600 transition-colors"
                >
                  Due Date
                  <SortIcon columnKey="due_date" />
                </button>
              </th>
              <th className="text-left px-4 py-3 text-sm font-semibold text-surface-700 border-b border-surface-200 w-36">
                <button
                  onClick={() => handleSort('assignee')}
                  className="flex items-center gap-1 hover:text-brand-600 transition-colors"
                >
                  Assignee
                  <SortIcon columnKey="assignee" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedTasks.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-12 text-center text-surface-500">
                  {searchQuery || filterPriority || filterStatus 
                    ? 'No tasks match your filters'
                    : 'No tasks yet. Click "Add Task" to create one.'}
                </td>
              </tr>
            ) : (
              sortedTasks.map(task => (
                <tr key={task.id} className="hover:bg-surface-50 transition-colors group">
                  {/* Title */}
                  <td className="px-4 py-3 border-b border-surface-100">
                    {editingCell?.taskId === task.id && editingCell?.field === 'title' ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="flex-1 px-2 py-1 border border-brand-500 rounded focus:outline-none focus:ring-2 focus:ring-brand-500"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit(task.id)
                            if (e.key === 'Escape') handleCancelEdit()
                          }}
                        />
                        <button onClick={() => handleSaveEdit(task.id)} className="p-1 text-green-600 hover:bg-green-50 rounded">
                          <HiCheck className="w-4 h-4" />
                        </button>
                        <button onClick={handleCancelEdit} className="p-1 text-red-600 hover:bg-red-50 rounded">
                          <HiX className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/tasks/${task.id}`}
                          className="font-medium text-surface-800 hover:text-brand-600 transition-colors"
                        >
                          {task.title}
                        </Link>
                        <button
                          onClick={() => handleStartEdit(task.id, 'title', task.title)}
                          className="p-1 text-surface-400 hover:text-brand-600 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <HiPencil className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3 border-b border-surface-100">
                    <select
                      value={task.columnId}
                      onChange={(e) => handleStatusChange(task.id, e.target.value)}
                      className="px-2 py-1 text-sm bg-surface-100 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 cursor-pointer"
                    >
                      {columns.map(col => (
                        <option key={col.id} value={col.id}>{col.name}</option>
                      ))}
                    </select>
                  </td>

                  {/* Priority */}
                  <td className="px-4 py-3 border-b border-surface-100">
                    <select
                      value={task.priority || ''}
                      onChange={(e) => handlePriorityChange(task.id, e.target.value || null)}
                      className={`px-2 py-1 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 cursor-pointer ${
                        task.priority ? PRIORITY_CONFIG[task.priority].color : 'bg-surface-50 border-surface-200'
                      }`}
                    >
                      <option value="">None</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                  </td>

                  {/* Due Date */}
                  <td className="px-4 py-3 border-b border-surface-100">
                    <input
                      type="date"
                      value={task.due_date ? format(new Date(task.due_date), 'yyyy-MM-dd') : ''}
                      onChange={async (e) => {
                        const newDate = e.target.value || null
                        // Optimistic update
                        updateTaskLocally(task.id, { due_date: newDate })
                        try {
                          await tasksApi.update(task.id, { 
                            due_date: newDate 
                          })
                        } catch (error) {
                          console.error('Failed to update due date:', error)
                          fetchBoardData()
                        }
                      }}
                      className={`px-2 py-1 text-sm bg-transparent border border-transparent hover:border-surface-200 rounded focus:outline-none focus:ring-2 focus:ring-brand-500 cursor-pointer ${
                        task.due_date && new Date(task.due_date) < new Date() ? 'text-red-600' : 'text-surface-700'
                      }`}
                    />
                  </td>

                  {/* Assignee */}
                  <td className="px-4 py-3 border-b border-surface-100 dark:border-surface-700">
                    <div className="flex items-center gap-2">
                      {task.assignee && (
                        <div className="w-6 h-6 rounded-full bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 flex items-center justify-center text-xs font-medium shrink-0">
                          {task.assignee.display_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                      )}
                      <select
                        value={task.assignee_id || ''}
                        onChange={(e) => handleAssigneeChange(task.id, e.target.value)}
                        className="px-2 py-1 text-sm bg-transparent border border-transparent hover:border-surface-200 dark:hover:border-surface-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 cursor-pointer text-surface-700 dark:text-surface-300 max-w-[120px] truncate"
                      >
                        <option value="">Unassigned</option>
                        {orgUsers.map(u => (
                          <option key={u.id} value={u.id}>{u.display_name}</option>
                        ))}
                      </select>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Summary */}
      <div className="flex items-center justify-between px-2 py-2 text-sm text-surface-500">
        <span>
          Showing {sortedTasks.length} of {allTasks.length} tasks
        </span>
      </div>

      {/* Create Task Modal */}
      {showCreateTask && (
        <CreateTaskModal
          columnId={columns[0]?.id}
          onClose={() => setShowCreateTask(false)}
          onCreate={handleCreateTask}
        />
      )}
    </div>
  )
}
